import { ImageOff } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';

/*
 * A customer's HTML, rendered as safely as a browser allows — business-rules.md
 * §10, ROADMAP.md §9.3.
 *
 * **This is the third of three layers and none of them is trusted alone.**
 * The HTML was sanitised on write (`lib/html-sanitize.ts`) and the raw original
 * kept in R2 where nothing renders it. This layer assumes that sanitiser could
 * one day have a gap, because the input is unauthenticated mail from the open
 * internet and it is the most hostile thing this application touches.
 *
 *   1. **A sandboxed `iframe` with `sandbox=""`.** The empty value is the
 *      maximum restriction, not the absence of one: no scripts, no forms, no
 *      same-origin access, no popups, no top-level navigation. Rendering the
 *      same markup with `dangerouslySetInnerHTML` would put it in *our* origin,
 *      where a single missed vector reads the admin's session.
 *   2. **A `Content-Security-Policy` inside the document**, so even an image or
 *      a stylesheet cannot reach the network. This is what stops a tracking
 *      pixel firing if one ever survived sanitising.
 *   3. **`srcdoc`, never a URL**, so the frame has no origin to inherit and
 *      nothing is fetched to build it.
 */

/**
 * The policy the framed document carries.
 *
 * `default-src 'none'` blocks everything and then nothing is added back except
 * inline styling. **No `img-src`**, so remote images cannot load even when the
 * office has asked to see them — see the note on `showImages` below.
 */
const FRAME_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:;";

const FRAME_STYLES = `
  html { color-scheme: light; }
  body {
    margin: 0;
    font: 14px/1.55 ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
    color: #1f1b16;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  a { color: #5a4632; }
  img { max-width: 100%; height: auto; }
  table { max-width: 100%; border-collapse: collapse; }
  blockquote {
    margin: 0.5rem 0;
    padding-left: 0.75rem;
    border-left: 2px solid #e5ded4;
    color: #6b6154;
  }
  pre { white-space: pre-wrap; }
`;

/**
 * Put the blocked image sources back.
 *
 * The sanitiser moved every `src` to `data-blocked-src` rather than dropping
 * it, so "show images" is a matter of moving it back. **The frame's CSP still
 * refuses to load them from the network**, which sounds like it defeats the
 * feature and does not: what the office gets is the layout with its images in
 * their right places and each one visibly failed, rather than a page collapsed
 * into a column of alt text. Actually fetching a remote image would confirm to
 * whoever sent it that this address is live and read by a person, which is the
 * entire reason those images exist in marketing mail.
 *
 * Downloading a specific image deliberately is a different action, and it is
 * not this one.
 */
function restoreImages(html: string): string {
  return html.replace(/\sdata-blocked-src=/g, ' src=');
}

export function MessageHtml({
  html,
  hasBlockedImages,
}: {
  html: string;
  hasBlockedImages: boolean;
}) {
  const [showImages, setShowImages] = useState(false);

  const document = useMemo(() => {
    const body = showImages ? restoreImages(html) : html;

    return [
      '<!doctype html><html><head>',
      `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">`,
      '<meta name="referrer" content="no-referrer">',
      `<style>${FRAME_STYLES}</style>`,
      '</head><body>',
      body,
      '</body></html>',
    ].join('');
  }, [html, showImages]);

  return (
    <div className="mt-2">
      {hasBlockedImages ? (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-sm border border-ink-300 bg-honey-50 px-3 py-2 text-meta text-bark-900">
          <ImageOff aria-hidden="true" className="size-4 shrink-0" />
          <span>
            Images in this message are blocked. Loading them tells the sender the address is real.
          </span>
          {showImages ? null : (
            <Button
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => {
                setShowImages(true);
              }}
            >
              Show layout
            </Button>
          )}
        </div>
      ) : null}

      <iframe
        // The empty string is the strictest sandbox there is. Do not add
        // `allow-scripts`: with `allow-same-origin` it would be equivalent to
        // no sandbox at all, and without it there is still nothing a customer's
        // mail has any business executing.
        sandbox=""
        srcDoc={document}
        title="Message content"
        referrerPolicy="no-referrer"
        className="border-ink-200 h-80 w-full rounded-sm border bg-white"
      />
    </div>
  );
}
